"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import {
  Bell,
  Bot,
  Boxes,
  Building2,
  CheckCircle2,
  Eye,
  Lock,
  Microscope,
  Rocket,
  Search,
  Shield,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import Link from "next/link";
// Import common layout elements
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

const heroPrompts = [
  "Extract every debt covenant from this 10-K.",
  "Build a DCF with bear, base, and bull cases.",
  "Alert me when volume and volatility break trend.",
  "Brief me on what changed since the last filing.",
];

// Hero Section for Kautious
const KautiousHeroSection = () => (
  <section className="relative w-full px-4 py-24 mx-auto max-w-screen-xl text-center overflow-hidden">
    {/* Enhanced Background Elements */}
    <div className="absolute inset-0 bg-grid-green/10 [mask-image:radial-gradient(white,transparent_70%)] pointer-events-none" />
    <div className="absolute inset-0 bg-geometric pointer-events-none" />
    <div className="absolute inset-0 floating-orbs pointer-events-none" />

    {/* Floating Financial Icons */}
    <div className="absolute top-20 left-12 w-16 h-16 bg-green-500/5 rounded-lg flex items-center justify-center animate-float">
      <Boxes className="w-8 h-8 text-green-500/30" />
    </div>
    <div
      className="absolute top-28 right-16 w-14 h-14 bg-emerald-500/5 rounded-full flex items-center justify-center animate-float"
      style={{ animationDelay: "2s" }}
    >
      <Search className="w-7 h-7 text-emerald-500/30" />
    </div>
    <div
      className="absolute bottom-32 left-16 w-12 h-12 bg-teal-500/5 rounded-lg flex items-center justify-center animate-float"
      style={{ animationDelay: "4s" }}
    >
      <Shield className="w-6 h-6 text-teal-500/30" />
    </div>
    <div
      className="absolute bottom-20 right-20 w-14 h-14 bg-green-600/5 rounded-full flex items-center justify-center animate-float"
      style={{ animationDelay: "1s" }}
    >
      <TrendingUp className="w-7 h-7 text-green-600/30" />
    </div>

    <div className="relative z-10">
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-6">
        Financial intelligence without the research drag
      </p>
      <h1 className="text-3xl font-bold leading-tight tracking-tighter md:text-5xl lg:text-6xl animate-gradient bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        Find the signal.
        <br /> Before the market does.
      </h1>
      <p className="mt-5 text-xl md:text-2xl font-semibold text-foreground">
        Turn research drag into decision speed.
      </p>
      <p className="mt-6 text-lg text-muted-foreground md:text-xl max-w-3xl mx-auto">
        Turn filings, market data, and plain-English requests into
        source-grounded research, live monitors, and working financial tools.
        Spend less time collecting the facts — and more time deciding what they
        mean.
      </p>
      <p className="mt-4 text-base text-muted-foreground/90 max-w-2xl mx-auto">
        Five-tier document parsing. Persistent research memory. Read-only
        portfolio analysis. Real artifacts, not chat answers.
      </p>

      <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
        {heroPrompts.map((prompt) => (
          <span
            key={prompt}
            className="text-xs md:text-sm text-muted-foreground/90 border border-border/70 bg-background/50 rounded-full px-3 py-1.5"
          >
            {prompt}
          </span>
        ))}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
        <Button
          size="lg"
          className="text-lg px-8 py-4 shimmer card-hover"
          asChild
        >
          <a href={CONTACT_URL} target="_blank" rel="noreferrer">
            Request Desk Access
          </a>
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-lg px-8 py-4 card-hover"
          asChild
        >
          <Link href="#pillars">See How the Edge Is Built</Link>
        </Button>
      </div>
      <div className="mt-6 text-sm text-muted-foreground italic">
        Hands-on onboarding for financial teams with a live workflow.
      </div>
    </div>
  </section>
);

// The 60-Second Win
const SixtySecondWin = () => (
  <section className="relative py-24 bg-secondary/5">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Cut the work between a question and a defensible answer.
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Open Kautious and ask for the research, model, monitor, or workflow
          you need. Inspect the evidence, revise the output in plain English,
          and approve it when it is ready for the desk.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3 mb-10">
        {[
          "Deep research on NVDA earnings outlook.",
          "Build a position-sizing calculator for the desk.",
          "Generate a market alert when volume spikes above trend.",
          "Screen these entities for KYC and escalate anything suspicious.",
        ].map((line) => (
          <div
            key={line}
            className="flex items-center gap-3 p-4 rounded-lg bg-background border border-border/50"
          >
            <span className="text-green-600 font-mono text-sm">&gt;</span>
            <p className="text-foreground">{line}</p>
          </div>
        ))}
      </div>

      <p className="text-center text-muted-foreground font-medium">
        Less data wrangling. Faster research cycles. Every conclusion tied back
        to evidence.
      </p>
    </div>
  </section>
);

// The Enemy
const TheEnemy = () => (
  <section className="relative py-24">
    <div className="max-w-4xl mx-auto px-6 text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-6">
        Your analysts are too valuable to spend their day moving data.
      </h2>
      <div className="space-y-3 text-lg text-muted-foreground mb-8">
        <p>
          Manual extraction burns expensive hours. The filing keeps growing.
        </p>
        <p>You needed one more field in the model. You got a ticket number.</p>
        <p>You needed the answer before the meeting. The market kept moving.</p>
      </div>
      <p className="text-lg text-foreground max-w-3xl mx-auto">
        Kautious gives expensive judgment more leverage. It automates the
        repeatable work, keeps the source trail visible, and turns one-off
        research into a controlled process the whole desk can reuse.
      </p>
    </div>
  </section>
);

// Product Pillars
const pillars = [
  {
    icon: Boxes,
    title: "Build",
    description:
      "Create sheets, streams, apps, reports, forecasts, documents, and caseflows from natural language. Preview first. Modify in plain English. Approve when ready.",
  },
  {
    icon: Search,
    title: "Research",
    description:
      "Work with SEC filings, XBRL, entity extraction, research notes, forecasts, watchlists, positions, and persistent memory in one workspace.",
  },
  {
    icon: Bell,
    title: "Monitor",
    description:
      "Track watchlists, market conditions, price and volume alerts, deployed streams, events, and workflow outcomes before they become meeting surprises.",
  },
  {
    icon: Bot,
    title: "Agents",
    description:
      "Run analyst, auditor, autonomous, managed, and swarm-style agents against real workflows with visible progress, task history, and human approval gates.",
  },
  {
    icon: Rocket,
    title: "Deploy",
    description:
      "Treat generated tools like software: preview, promote, monitor, roll back, and keep a clean record of what changed and why.",
  },
  {
    icon: ShieldCheck,
    title: "Audit",
    description:
      "Preserve reasoning, tool calls, decisions, permissions, event history, and workspace activity so the work can survive scrutiny.",
  },
];

const ProductPillars = () => (
  <section id="pillars" className="relative py-24 bg-secondary/5">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          From prompt to deployed financial tool.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {pillars.map((pillar) => {
          const Icon = pillar.icon;
          return (
            <Card key={pillar.title} className="p-8 card-hover">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{pillar.title}</h3>
              <p className="text-muted-foreground">{pillar.description}</p>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

// Ego & Status
const EgoStatus = () => (
  <section className="relative py-24">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-12 max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Walk into the investment committee with evidence, not tabs.
        </h2>
        <p className="text-lg text-muted-foreground">
          Bring the model, the filing evidence, the forecast, and the decision
          trace into the room. Every conclusion is easier to defend when the
          work behind it is already organized.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {[
          {
            title: "Ship the tool the team adopts",
            body: "Your screener, your dashboard, your alert stream, your caseflow — deployed and reusable.",
          },
          {
            title: "Show the work behind the conclusion",
            body: "Bring the model, filing evidence, forecast, decision trace, and alert history into the room.",
          },
          {
            title: "Turn AI into operating leverage",
            body: "Not a slide about 'exploring AI' — a library of tools, agents, workflows, and reports already running inside your process.",
          },
          {
            title: "Compound your edge",
            body: "Every artifact you build becomes context for the next one: templates, reusable workflows, and memory your agents carry forward.",
          },
        ].map((item) => (
          <Card key={item.title} className="p-6 card-hover">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.body}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// Use Cases
const useCases = [
  {
    icon: Microscope,
    role: "For Analysts",
    body: "Move from idea to model, filing summary, thesis check, watchlist brief, or research workflow without rebuilding the same process by hand.",
  },
  {
    icon: TrendingUp,
    role: "For Portfolio Managers",
    body: "Monitor risks, positions, market events, and research follow-ups across the process without adding another passive dashboard.",
  },
  {
    icon: Users,
    role: "For Research Teams",
    body: "Standardize repeatable workflows while letting each analyst build the tools they need for their coverage.",
  },
  {
    icon: ShieldCheck,
    role: "For Operators, CFOs & Compliance",
    body: "Run caseflows such as KYC screening, GL reconciliation, statement audits, and month-end workflows with evidence, approvals, and escalation paths.",
  },
  {
    icon: Building2,
    role: "For CIOs",
    body: "Be the executive who delivered AI leverage with governance attached: approvals, audit trails, read-only access — instead of shadow tools built without you.",
  },
];

const UseCases = () => (
  <section className="relative py-24 bg-secondary/5">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Built for the whole desk.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {useCases.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.role} className="p-8 card-hover">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{item.role}</h3>
              <p className="text-muted-foreground">{item.body}</p>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

// De-Risking
const DeRisking = () => (
  <section className="relative py-24">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Read-only by architecture. Auditable by default.
        </h2>
        <p className="text-lg text-muted-foreground">
          Kautious can connect to portfolio and market data, but it is not a
          trading platform. There is no trade execution capability in the
          product. This is the AI deployment a serious desk can say yes to.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            icon: Eye,
            title: "Read-only portfolio access",
            body: "Connect and analyze. Execute nothing.",
          },
          {
            icon: ShieldCheck,
            title: "Human approval",
            body: "Generated work goes through preview and approval flows.",
          },
          {
            icon: Lock,
            title: "Explicit permissions",
            body: "Workspace actions are scoped and auditable.",
          },
          {
            icon: Shield,
            title: "Every action traced",
            body: "Every consequential action leaves an audit trail.",
          },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.title} className="p-6 text-center card-hover">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.body}</p>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

// CTA / Velvet Rope
const CTASection = () => (
  <section className="relative py-24 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
    <div className="max-w-screen-xl mx-auto px-6 text-center">
      <div className="inline-flex items-center gap-2 mb-4 text-white/90">
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-medium uppercase tracking-[0.2em]">
          Hands-on desk onboarding
        </span>
      </div>
      <h2 className="text-3xl md:text-4xl font-bold mb-6">
        The market will not wait for your data team.
      </h2>
      <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
        Bring us a live research workflow. We&apos;ll show you where Kautious
        removes the drag and keeps the evidence.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button
          size="lg"
          variant="secondary"
          className="text-lg px-8 py-4"
          asChild
        >
          <a href={CONTACT_URL} target="_blank" rel="noreferrer">
            Request Desk Access
          </a>
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-lg px-8 py-4 bg-transparent text-white border-white hover:bg-white/10"
          asChild
        >
          <Link href="/desktop">Explore the desktop app</Link>
        </Button>
      </div>
    </div>
  </section>
);

export default function KautiousPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header - Replicated for standalone page context */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="relative">
        <KautiousHeroSection />
        <SixtySecondWin />
        <TheEnemy />
        <ProductPillars />
        <EgoStatus />
        <UseCases />
        <DeRisking />
        <CTASection />
      </main>

      {/* Footer - Replicated for standalone page context */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex flex-col items-center gap-6">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-2xl">
              Kautious provides analytics and workflow tooling. It is not
              investment advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
